import { Controller } from "@hotwired/stimulus"

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

export default class extends Controller {
  static values = { message: String, proceedLabel: String, tone: String }

  open(event) {
    event.preventDefault()

    const msg = this.messageValue || 'Are you sure?'
    const proceed = this.proceedLabelValue || 'Remove'
    const tone = this.toneValue === 'destructive' ? 'destructive' : 'default'

    this._buildModal(msg, proceed, tone)
  }

  _buildModal(msg, proceed, tone) {
    const cancelText = proceed ? 'Cancel' : 'OK'
    const proceedClass = tone === 'destructive'
      ? 'px-3 py-1 rounded-[6px] bg-[var(--color-danger)] text-white'
      : 'px-3 py-1 rounded-[6px] bg-[var(--color-clay)] text-white'
    const focusOutline = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-clay)]'
    const messageId = `confirm-message-${Math.random().toString(36).slice(2)}`

    this.previouslyFocused = document.activeElement

    this.wrapper = document.createElement('div')
    this.wrapper.innerHTML = `
      <div class="fixed inset-0 bg-black/40 z-40" data-confirm-target="overlay"></div>
      <div class="fixed inset-0 z-50 flex items-center justify-center">
        <div class="bg-[var(--color-surface)] text-[var(--color-ink)] rounded-[6px] shadow-lg p-4 max-w-sm w-full"
             role="alertdialog" aria-modal="true" aria-describedby="${messageId}" data-confirm-target="dialog">
          <p id="${messageId}" class="mb-4">${msg}</p>
          <div class="flex justify-end space-x-2">
            <button data-confirm-target="cancel" class="px-3 py-1 rounded-[6px] border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-ink)] ${focusOutline}">${cancelText}</button>
            ${proceed ? `<button data-confirm-target="proceed" class="${proceedClass} ${focusOutline}">${proceed}</button>` : ''}
          </div>
        </div>
      </div>
    `
    document.body.appendChild(this.wrapper)
    this.wrapper.querySelector('[data-confirm-target="overlay"]').addEventListener('click', this.cancel.bind(this))
    this.wrapper.querySelector('[data-confirm-target="cancel"]').addEventListener('click', this.cancel.bind(this))
    if (proceed) {
      this.wrapper.querySelector('[data-confirm-target="proceed"]').addEventListener('click', this.proceed.bind(this))
    }

    this.onKeydown = this._onKeydown.bind(this)
    document.addEventListener('keydown', this.onKeydown, true)

    // Cancel is the safer default landing spot for a destructive confirmation
    // — it never fires the guarded action on a stray Enter/Space.
    this.wrapper.querySelector('[data-confirm-target="cancel"]').focus()
  }

  _onKeydown(event) {
    if (!this.wrapper) return

    if (event.key === 'Escape') {
      event.preventDefault()
      // Without this, the same Escape keypress would bubble past the
      // dialog to whatever's behind it (e.g. the <details> menu the
      // triggering button lives in) and close that too.
      event.stopPropagation()
      this.cancel()
      return
    }

    if (event.key !== 'Tab') return

    const focusable = Array.from(this.wrapper.querySelectorAll(FOCUSABLE_SELECTOR))
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    } else if (!focusable.includes(document.activeElement)) {
      // Focus somehow escaped the dialog (e.g. programmatic focus elsewhere) —
      // pull it back in rather than let Tab continue into the page behind it.
      event.preventDefault()
      first.focus()
    }
  }

  _teardown() {
    document.removeEventListener('keydown', this.onKeydown, true)
    this.wrapper?.remove()
    this.wrapper = null
    this.previouslyFocused?.focus?.()
  }

  cancel() {
    this._teardown()
  }

  proceed() {
    const form = this.element.closest('form')
    this._teardown()

    const organizeField = form && form.querySelector('input[name="organize"]')
    if (organizeField) {
      organizeField.value = 'true'
    }
    form.requestSubmit()
  }
}
